import { mat3, vec4, vec2 } from "gl-matrix"
import {
	writeHSLPointColorData,
	HSLPointColorDataSize,
	HSLPointColorMaxCount,
	PerPointColorSize,
} from "../hsl-point-color"
import {
	Adjustments,
	LookTable,
	LookTableRGBGamma,
	LookTableRGBPrimaries,
	LookTableType,
	MaskGroup,
} from "../types"

const mat3Length = 12
const vec3Length = 4
const vec4Length = 4

function getPaddedMat3(mat: mat3) {
	return new Float32Array([
		mat[0],
		mat[1],
		mat[2],
		1,
		mat[3],
		mat[4],
		mat[5],
		1,
		mat[6],
		mat[7],
		mat[8],
		1,
	])
}

const GlobalAdjustmentsDataSize = 280 // 30 * 4 + 160 (HSLPointColorDataSize)
export const GlobalAdjustmentsBufferSize = GlobalAdjustmentsDataSize * 4
const globalBufferData = new Float32Array(GlobalAdjustmentsDataSize)

export function getGlobalAdjustmentData(
	adjustments: Adjustments,
	infos: {
		whiteBalanceMatrix: mat3
		workingSpaceMatrix: mat3
		globalColor: vec4
		shadowsColor: vec4
		midtonesColor: vec4
		highlightsColor: vec4
		imageSize: vec2
		adjustedTemp: number
		adjustedTint: number
		maskCount: number
		renderLocalContrast: boolean
		renderLocalWB: boolean
		userLut?: LookTable
		exposureBias: number
	},
): Float32Array {
	const {
		whiteBalanceMatrix,
		workingSpaceMatrix,
		globalColor,
		shadowsColor,
		midtonesColor,
		highlightsColor,
		imageSize,
		adjustedTemp,
		adjustedTint,
		maskCount,
		renderLocalContrast,
		renderLocalWB,
		userLut,
	} = infos

	let offset = 0

	globalBufferData.set(getPaddedMat3(whiteBalanceMatrix), offset)
	offset += mat3Length

	globalBufferData.set(getPaddedMat3(workingSpaceMatrix), offset)
	offset += mat3Length

	globalBufferData.set(
		[userLut?.hueDivisions ?? 0, userLut?.satDivisions ?? 0, userLut?.valDivisions ?? 0],
		offset,
	)
	offset += vec3Length

	globalBufferData.set(globalColor, offset)
	offset += vec4Length
	globalBufferData.set(shadowsColor, offset)
	offset += vec4Length
	globalBufferData.set(midtonesColor, offset)
	offset += vec4Length
	globalBufferData.set(highlightsColor, offset)
	offset += vec4Length

	writeHSLPointColorData(adjustments, globalBufferData, offset)
	offset += HSLPointColorDataSize

	globalBufferData[offset++] = imageSize[0]
	globalBufferData[offset++] = imageSize[1]
	globalBufferData[offset++] = adjustedTemp
	globalBufferData[offset++] = adjustedTint

	globalBufferData[offset++] = adjustments.exposure + infos.exposureBias
	globalBufferData[offset++] = adjustments.shadows
	globalBufferData[offset++] = adjustments.highlights
	globalBufferData[offset++] = adjustments.whites

	globalBufferData[offset++] = adjustments.blacks
	globalBufferData[offset++] = adjustments.saturation
	globalBufferData[offset++] = adjustments.vibrance
	globalBufferData[offset++] = adjustments.clarity

	globalBufferData[offset++] = adjustments.texture

	globalBufferData[offset++] = adjustments.vignette
	globalBufferData[offset++] = adjustments.vignetteFeather
	globalBufferData[offset++] = adjustments.vignetteMidpoint
	globalBufferData[offset++] = adjustments.vignetteRoundness
	globalBufferData[offset++] = adjustments.vignetteHighlights

	globalBufferData[offset++] = adjustments.dehaze

	globalBufferData[offset++] = adjustments.sharpen
	globalBufferData[offset++] = adjustments.lensDistortion
	globalBufferData[offset++] = adjustments.gradingBlending
	globalBufferData[offset++] = adjustments.gradingBalance

	globalBufferData[offset++] = adjustments.grainAmount
	globalBufferData[offset++] = adjustments.grainSize
	globalBufferData[offset++] = adjustments.grainRoughness

	globalBufferData[offset++] = userLut?.type ?? LookTableType.unknown
	globalBufferData[offset++] = adjustments.userLutStrength
	globalBufferData[offset++] = userLut?.rgbGamma ?? LookTableRGBGamma.invalid
	globalBufferData[offset++] = userLut?.rgbPrimaries ?? LookTableRGBPrimaries.invalid

	globalBufferData[offset++] = adjustments.pointColors?.length ?? 0
	globalBufferData[offset++] = maskCount
	globalBufferData[offset++] = renderLocalContrast ? 1 : 0

	globalBufferData[offset++] = renderLocalWB ? 1 : 0

	globalBufferData[offset++] = adjustments.defringePurpleAmount
	globalBufferData[offset++] = adjustments.defringeGreenAmount
	globalBufferData[offset++] = adjustments.defringePurpleHueRange[0]
	globalBufferData[offset++] = adjustments.defringePurpleHueRange[1]
	globalBufferData[offset++] = adjustments.defringeGreenHueRange[0]
	globalBufferData[offset++] = adjustments.defringeGreenHueRange[1]

	return globalBufferData
}

export const LocalAdjustmentsMaxCount = 50
export const PerLocalDataSize = 16
export const LocalPointColorMaxCountTotal = 50

// Note: here PerLocalDataSize == PerPointColorSize == 16 so no padding needed
const LocalParamsSize = Math.max(PerLocalDataSize, PerPointColorSize)
export const LocalParamsTextureWidth = Math.ceil(LocalParamsSize / 4)
export const LocalParamsTextureHeight = LocalAdjustmentsMaxCount + LocalPointColorMaxCountTotal

const localBufferData = new Float32Array(LocalParamsTextureHeight * LocalParamsSize)

export function getLocalAdjustmentsData(localAdjustments: MaskGroup[] | undefined): Float32Array {
	if (!localAdjustments) {
		return localBufferData
	}

	const count = Math.min(localAdjustments.length, LocalAdjustmentsMaxCount)

	for (let i = 0; i < count; i++) {
		const adjustments = localAdjustments[i] as MaskGroup
		const strength = adjustments.strength
		const baseIndex = i * PerLocalDataSize

		localBufferData[baseIndex + 0] = adjustments.temperature * strength
		localBufferData[baseIndex + 1] = adjustments.tint * strength
		localBufferData[baseIndex + 2] = adjustments.saturation * strength
		localBufferData[baseIndex + 3] = adjustments.toningHue * strength

		localBufferData[baseIndex + 4] = adjustments.toningSaturation * strength
		localBufferData[baseIndex + 5] = adjustments.exposure * strength
		localBufferData[baseIndex + 6] = adjustments.contrast * strength
		localBufferData[baseIndex + 7] = adjustments.highlights * strength

		localBufferData[baseIndex + 8] = adjustments.shadows * strength
		localBufferData[baseIndex + 9] = adjustments.whites * strength
		localBufferData[baseIndex + 10] = adjustments.blacks * strength
		localBufferData[baseIndex + 11] = adjustments.clarity * strength

		localBufferData[baseIndex + 12] = adjustments.texture * strength
		localBufferData[baseIndex + 13] = adjustments.dehaze * strength
		localBufferData[baseIndex + 14] = adjustments.sharpen * strength
		localBufferData[baseIndex + 15] = adjustments.pointColors?.length ?? 0
	}

	let offset = LocalAdjustmentsMaxCount * LocalParamsSize
	for (let i = 0; i < count; i++) {
		const adjustments = localAdjustments[i] as MaskGroup

		writeHSLPointColorData(adjustments, localBufferData, offset)

		const pointColorsCount = Math.min(
			adjustments.pointColors?.length ?? 0,
			HSLPointColorMaxCount,
		)
		offset += pointColorsCount * PerPointColorSize
	}

	return localBufferData
}
